import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Shield, AlertTriangle, Heart, Zap, Utensils, MessageCircle, Activity, ClipboardList, Presentation, Bone, FlaskConical, Brain, ThumbsUp, ThumbsDown } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

// V2 Theme Colors
const C = {
  white: '#FFFFFF',
  cloud: '#F8FAFC',
  sky: '#4A6D8C',
  skyLight: '#DCE6F0',
  skyDark: '#334F6B',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
};

const CausesPage = () => {
  const parasympatheticFeatures = [
    {
      icon: Heart,
      title: "Relaxation",
      description: "État de calme et de détente profonde du corps et de l'esprit"
    },
    {
      icon: Utensils,
      title: "Digestion",
      description: "Optimisation de la fonction digestive et de l'absorption des nutriments"
    },
    {
      icon: MessageCircle,
      title: "Interactions Sociales",
      description: "Capacité à communiquer, créer des liens et s'ouvrir aux autres"
    }
  ];

  const sympatheticFeatures = [
    {
      hormone: "Adrénaline",
      effects: [
        "Augmentation de la fréquence cardiaque",
        "Dilatation des bronches",
        "Libération rapide d'énergie",
        "Préparation à l'action immédiate"
      ],
      color: "from-slate-500 to-slate-600"
    },
    {
      hormone: "Cortisol",
      effects: [
        "Augmentation de la glycémie",
        "Suppression du système immunitaire",
        "Augmentation de la tension artérielle",
        "Mobilisation des réserves énergétiques"
      ],
      color: "from-slate-500 to-slate-600"
    }
  ];

  return (
    <div 
      className="min-h-screen" 
      style={{ backgroundColor: C.white, fontFamily: 'Figtree, sans-serif' }}
    >
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
      `}</style>
      
      <SiteHeaderV2 />
      
      {/* Hero Section */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-6xl mx-auto">
            <PageTitle
              kicker="Comprendre"
              title="Causes des Troubles"
              highlight="Respiratoires"
              subtitle="Comprendre le rôle du système nerveux autonome dans les troubles respiratoires"
              align="center"
            />
            <div className="text-center mt-12">
              <Link 
                to="/evaluer-respiration"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                style={{ backgroundColor: C.sky, color: C.white }}
              >
                Évaluer votre respiration
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* New Introduction Section */}
      <section className="py-12 ">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="prose prose-lg max-w-none">
            {/* Patient testimonial */}
            <blockquote className="bg-sky-50 border-l-4 border-sky-500 p-6 rounded-r-2xl mb-8 italic text-slate-700">
              <p className="text-lg leading-relaxed mb-0">
                &ldquo;J&apos;éprouve un inconfort respiratoire au quotidien, je suis vite essoufflé à l&apos;effort, voire juste en parlant, ou en mangeant, j&apos;ai l&apos;impression de ne pas arriver à prendre suffisamment d&apos;air&rdquo;
              </p>
            </blockquote>
            
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Voilà les paroles des patients que je reçois chaque jour, et une affirmation revient souvent : <em>&ldquo;On me dit que je ne sais pas respirer, je respire tout faux&rdquo;</em>...
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Eh bien avant tout, il faut comprendre une chose cruciale, <strong className="text-sky-700 text-xl">VOUS ne respirez PAS</strong>...
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Non, ce n&apos;est pas vous qui respirez, c&apos;est le corps qui respire, et bien heureusement, il fait ça tout seul, comme un grand, comme de faire battre le cœur, de gérer la digestion, de permettre la perception d&apos;images, de sons, de sensations, d&apos;initier un mouvement, de dormir ou d&apos;aller aux toilettes... En fait, <strong className="text-sky-700">VOUS ne faites RIEN !</strong>
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Le corps est une machine à vivre, le résultat de millions d&apos;années d&apos;évolution, une machine dont la compétence principale est la capacité à s&apos;auto-entretenir, résister aux agressions du monde extérieur et intérieur, s&apos;auto-réparer, cicatriser, trouver continuellement les ressources pour maintenir ou retrouver son équilibre, ce que l&apos;on appelle l&apos;<strong className="text-sky-600">homéostasie</strong>.
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Lorsque vous sentez votre cœur battre plus vite, plus fort, ça n&apos;est pas un problème, c&apos;est une <em>solution</em> que le cœur trouve pour maintenir l&apos;apport sanguin alors que celui-ci est menacé.
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Lorsque tout d&apos;un coup vos épaules se contractent, que la digestion se bloque, que vous n&apos;arrivez plus à dormir, que votre respiration s&apos;accélère, <strong className="text-slate-800">ce ne sont pas des problèmes</strong>.
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Ce sont des expressions ressenties d&apos;un corps qui se sent en danger, et qui se prépare à une agression, qu&apos;elle soit réelle ou... simplement imaginée.
            </p>

            {/* Key insight box */}
            <div className="bg-white/60 backdrop-blur-md border border-white/70 rounded-2xl p-6 mb-8 shadow-lg">
              <p className="text-sky-800 text-xl font-semibold text-center mb-0">
                Ce n&apos;est donc pas vous qui ne savez pas respirer, ce n&apos;est pas non plus le corps qui ne sait pas respirer,<br/>
                <span className="text-2xl text-slate-900">C&apos;est <strong>NOUS</strong> qui ne savons pas <strong>ÉCOUTER</strong> !</span>
              </p>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Écouter les messages du corps en comprenant que celui-ci est toujours juste. Écouter ses réactions, comprendre quand le corps se sent en sécurité, quand il se sent menacé, et ensuite, se poser la question : <strong className="text-sky-700">Par Quoi ?</strong>
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              On en revient alors à l&apos;identification des <strong>3 types de stresseurs</strong> qui tentent de déstabiliser l&apos;organisme et le forcent à s&apos;adapter :
            </p>

            {/* 3 Stressors */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-sky-100 border border-sky-300 rounded-xl p-4 text-center">
                <span className="text-3xl mb-2 block">🦴</span>
                <span className="font-medium text-lg">1. Mécaniques</span>
              </div>
              <div className="bg-sky-100 border border-green-300 rounded-xl p-4 text-center">
                <span className="text-3xl mb-2 block">🧪</span>
                <span className="font-bold text-sky-800 text-lg">2. Chimiques</span>
              </div>
              <div className="bg-sky-100 border border-sky-300 rounded-xl p-4 text-center">
                <span className="text-3xl mb-2 block"></span>
                <span className="font-medium text-lg">3. Psychologiques</span>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Voici le cœur de notre pratique, pour aider le corps à retrouver cet état de relâchement, où la respiration est calme, diaphragmatique, silencieuse et douce, où le cœur bat tranquillement, où la digestion se fait sans bruit, où le sommeil est facile, profond, réparateur...
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Non ce n&apos;est pas impossible, non ce n&apos;est pas si difficile, si vous comprenez l&apos;intelligence naturelle du corps, et que vous l&apos;écoutez, reconnaissez ses plaintes, et comprenez que, comme votre voiture, si vous l&apos;entretenez, si vous lui donnez les nutriments qui lui conviennent, si vous n&apos;appuyez pas continuellement sur l&apos;accélérateur sans jamais utiliser le frein...
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Si vous l&apos;utilisez conformément au manuel d&apos;utilisation que nous n&apos;avons jamais reçu mais qui pourtant existe, était connu par les sociétés anciennes et encore aujourd&apos;hui par les tribus qui n&apos;ont pas été polluées par l&apos;arrogance de la civilisation moderne, qui ne sait même plus comment manger, comment dormir, comment faire naître un enfant ni même s&apos;en occuper, qui a perdu le bon sens inné de tout organisme vivant.
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Les animaux se posent-ils la question de savoir quelle nourriture leur convient ? À quel moment se reproduire ? Combien de fois par jour il faudrait aller aux toilettes ?
            </p>

            {/* Final message */}
            <div className="bg-white/60 backdrop-blur-md border border-white/70 rounded-2xl p-6 mt-8 shadow-lg">
              <p className="text-slate-800 text-lg leading-relaxed mb-0">
                La proposition ici est un <strong>retour à l&apos;écoute</strong>, à la saveur de ressentir cette expérience de vie que nous permettent les sens, un retour au seul moment réel, <strong>CE moment</strong>, cet éternel maintenant... Et de permettre au corps de revenir à son état de base, un état de détente, de bien-être, <em>le seul état dans lequel le corps peut faire son travail</em>.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Evaluate ANS Button */}
          <div className="mt-8 flex flex-wrap gap-4">
            <a 
              href="/documents/Questionnaire_SNA.xlsx" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 shadow-lg transition-all hover:shadow-xl"
            >
              <ClipboardList size={24} />
              <span className="font-semibold">Évaluer son système nerveux autonome</span>
            </a>
            <a 
              href="/documents/Causes_Troubles_Respiratoires.pptx"
              download
              className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-slate-500 to-slate-600 text-white rounded-xl hover:from-orange-600 hover:to-red-600 shadow-lg transition-all hover:shadow-xl cursor-pointer"
            >
              <Presentation size={24} />
              <span className="font-semibold">Powerpoint complet sur les causes</span>
            </a>
          </div>
        </div>
      </section>

      {/* Section: Body's Adaptation & Stressors */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Introduction */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
              La Capacité d&apos;Adaptation du Corps
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Le corps possède une <strong className="text-sky-600">formidable capacité de s&apos;adapter</strong> et de maintenir ou restaurer la santé. 
              Mais s&apos;adapter à quoi ?
            </p>
          </div>

          {/* Stressors Explanation — frosted glass */}
          <div
            className="rounded-3xl p-8 mb-10 border border-white/60 shadow-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(224,242,254,0.45) 100%)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
            }}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-500 flex items-center justify-center flex-shrink-0">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-medium mb-2">Les Stresseurs</h3>
                <p className="text-lg text-slate-600">
                  Le corps est constamment soumis à des forces qui tendent à perturber son équilibre. 
                  On les appelle des <strong className="text-slate-700">stresseurs</strong>.
                </p>
              </div>
            </div>
            <p className="text-slate-600 text-center text-lg font-medium">
              Ces stresseurs se divisent en <strong>3 catégories principales</strong> :
            </p>
          </div>

          {/* Three Types of Stressors */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Traumatic */}
            <Card className="border-0 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="h-2 bg-gradient-to-r from-slate-400 to-slate-500" />
              <CardContent className="p-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center mx-auto mb-4">
                  <Bone className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-medium text-center mb-3">Traumatiques</h4>
                <p className="text-slate-600 text-center text-sm mb-4">
                  Agressions physiques sur le corps
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                    Accidents
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                    Fractures
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                    Opérations chirurgicales
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                    Cicatrices
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Chemical */}
            <Card className="border-0 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="h-2 bg-gradient-to-r from-sky-400 to-sky-500" />
              <CardContent className="p-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-500 flex items-center justify-center mx-auto mb-4">
                  <FlaskConical className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-medium text-center mb-3">Chimiques</h4>
                <p className="text-slate-600 text-center text-sm mb-4">
                  Substances affectant l&apos;organisme
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0"></span>
                    Alimentation inadaptée
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0"></span>
                    Toxiques environnementaux
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0"></span>
                    Médicaments
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0"></span>
                    Polluants
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Psychological */}
            <Card className="border-0 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="h-2 bg-gradient-to-r from-sky-500 to-sky-600" />
              <CardContent className="p-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-medium text-center mb-3">Psychologiques</h4>
                <p className="text-slate-600 text-center text-sm mb-4">
                  La puissance de nos pensées
                </p>
                <div className="space-y-3">
                  <div className="p-3 bg-sky-50 rounded-lg border border-sky-200">
                    <div className="flex items-center gap-2 mb-1">
                      <ThumbsUp className="w-4 h-4 text-sky-600" />
                      <span className="text-sm font-bold text-sky-700">Pensées Positives</span>
                    </div>
                    <p className="text-xs text-sky-600">
                      → Activation du système de relaxation
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 mb-1">
                      <ThumbsDown className="w-4 h-4 text-sky-600" />
                      <span className="text-sm font-bold text-sky-700">Pensées Négatives</span>
                    </div>
                    <p className="text-xs text-sky-600">
                      Peur, colère, frustration → Activation du système de stress
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Key Message */}
          <div className="mt-10 p-6 rounded-3xl rounded-2xl text-slate-900 text-center">
            <p className="text-lg font-medium">
              <strong>Le corps écoute nos pensées en permanence.</strong> Leur caractère positif ou négatif influence directement 
              l&apos;activation de notre système nerveux autonome.
            </p>
          </div>
        </div>
      </section>

      {/* The Body as a Life-Maintaining Machine */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
            <div
              className="flex items-start gap-4 p-6 rounded-2xl border border-white/60 shadow-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(224,242,254,0.45) 100%)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
              }}
            >
              <Shield className="w-12 h-12 text-sky-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-medium mb-3 mt-0 text-slate-900">Le Corps : Une Merveilleuse Machine à Maintenir la Vie</h2>
                <p className="text-lg mb-0 text-slate-800">
                  Notre corps est une machine extraordinaire dont l&apos;objectif premier est de <strong>maintenir la vie à tout prix</strong>. 
                  Pour accomplir cette mission, il dispose d&apos;un mécanisme primordial qui fonctionne 24 heures sur 24, 7 jours sur 7, 
                  sans que nous en ayons conscience : <strong>le système nerveux autonome</strong>.
                </p>
              </div>
            </div>

            <p className="text-lg leading-relaxed">
              Ce système agit comme un <strong>gardien vigilant</strong> qui scanne continuellement votre environnement 
              extérieur (ce qui se passe autour de vous) et intérieur (ce qui se passe dans votre corps), à la recherche 
              de signes de danger pour l&apos;intégrité du corps et le maintien de la vie.
            </p>

            <p className="text-lg leading-relaxed">
              Ce processus s&apos;appelle la <strong>neuroception</strong> : une évaluation inconsciente et automatique du niveau 
              de sécurité ou d&apos;insécurité de notre environnement, effectuée par notre système nerveux sans que notre cerveau 
              conscient n&apos;ait à intervenir.
            </p>
          </div>
        </div>
      </section>

      {/* The Two Modes */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Deux Modes Complètement Opposés
          </h2>
          
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            En fonction de la réponse à cette évaluation continue — <strong>&ldquo;Suis-je en sécurité ?&rdquo;</strong> ou 
            <strong> &ldquo;Suis-je en insécurité ?&rdquo;</strong> — le système nerveux active l&apos;un de ces deux modes.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Parasympathetic Mode - Safety */}
            <Card className="border-0 shadow-2xl overflow-hidden">
              <div className="h-3 bg-gradient-to-r from-sky-500 to-sky-600" />
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-sky-600 uppercase tracking-wide">Réponse</div>
                    <h3 className="text-2xl font-medium">SÉCURITÉ</h3>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="inline-block px-4 py-2 bg-sky-100 rounded-full mb-4">
                    <span className="text-sky-800 font-bold">Mode Parasympathique</span>
                  </div>
                  <p className="text-slate-600 text-sm mb-4">
                    Lorsque le système nerveux détecte la sécurité, il active le <strong>système parasympathique</strong>, 
                    également appelé système de &ldquo;repos et digestion&rdquo; ou &ldquo;restauration&rdquo;.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-sm uppercase tracking-wide mb-3">Fonctions Activées</h4>
                  {parasympatheticFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-sky-50 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-4 h-4 text-sky-600" />
                      </div>
                      <div>
                        <div className="font-medium text-sm">{feature.title}</div>
                        <div className="text-xs text-slate-600">{feature.description}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-sky-50 rounded-lg border-l-4 border-sky-500">
                  <p className="text-sm text-sky-900">
                    <strong>État optimal :</strong> C&apos;est dans cet état que le corps se régénère, guérit, 
                    digère correctement et que nous pouvons vivre des relations épanouissantes.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Sympathetic Mode - Danger */}
            <Card className="border-0 shadow-2xl overflow-hidden">
              <div className="h-3 bg-gradient-to-r from-slate-500 to-slate-600" />
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Réponse</div>
                    <h3 className="text-2xl font-medium">INSÉCURITÉ</h3>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="inline-block px-4 py-2 bg-sky-100 rounded-full mb-4">
                    <span className="text-slate-800 font-bold">Mode Sympathique</span>
                  </div>
                  <p className="text-slate-600 text-sm mb-4">
                    Lorsque le système nerveux détecte un danger (réel ou perçu), il active le <strong>système sympathique</strong>, 
                    également appelé système de &ldquo;combat ou fuite&rdquo;.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-sm uppercase tracking-wide mb-3">Hormones Libérées</h4>
                  {sympatheticFeatures.map((feature, index) => (
                    <div key={index} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-slate-600" />
                        <span className="font-medium">{feature.hormone}</span>
                      </div>
                      <div className="pl-7 space-y-1">
                        {feature.effects.map((effect, i) => (
                          <div key={i} className="text-xs text-slate-600 flex items-start gap-2">
                            <span className="text-slate-500 mt-0.5">•</span>
                            <span>{effect}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-sky-50 rounded-lg border-l-4 border-sky-500">
                  <p className="text-sm text-slate-900">
                    <strong>État de survie :</strong> Conçu pour être temporaire, ce mode nous prépare à réagir 
                    face au danger. Problème : il peut devenir chronique.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Polyvagal Theory */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            La Théorie Polyvagale du Dr. Stephen Porges
          </h2>

          <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
            <Card className="border-0 bg-sky-50 border-l-4 border-sky-500">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-sky-900 mb-3 mt-0">Une Révolution dans la Compréhension du Stress</h3>
                <p className="text-sky-800 mb-0">
                  Le Dr. Stephen Porges, neuroscientifique américain, a développé dans les années 1990 la <strong>théorie polyvagale</strong>, 
                  qui révolutionne notre compréhension du système nerveux autonome. Cette théorie démontre que le nerf vague 
                  (le principal nerf du système parasympathique) possède en réalité <strong>deux branches distinctes</strong> qui 
                  régulent des réponses physiologiques et comportementales très différentes.
                </p>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-medium">Les Trois États du Système Nerveux Autonome</h3>
            
            <p className="text-lg">
              Selon la théorie polyvagale, nous ne basculons pas simplement entre deux modes (sympathique et parasympathique), 
              mais entre <strong>trois états hiérarchiques</strong> qui se sont développés au cours de l&apos;évolution :
            </p>

            <div className="space-y-6">
              {/* State 1 - Ventral Vagal (Safety) */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xl">1</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-medium mb-2">Nerf Vague Ventral (Sécurité & Connexion Sociale)</h4>
                      <p className="text-slate-600 mb-3">
                        État de <strong>sécurité perçue</strong>. Le nerf vague ventral permet la relaxation, 
                        la connexion sociale, la communication verbale et faciale. C&apos;est l&apos;état optimal de fonctionnement.
                      </p>
                      <div className="p-3 bg-sky-50 rounded-lg">
                        <p className="text-sm text-sky-900 mb-1"><strong>Respiration :</strong></p>
                        <p className="text-sm text-sky-800">
                          Lente, profonde, diaphragmatique, régulière. Variation naturelle du rythme (cohérence cardiaque).
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* State 2 - Sympathetic (Mobilization) */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xl">2</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-medium mb-2">Système Sympathique (Mobilisation : Fuite ou Combat)</h4>
                      <p className="text-slate-600 mb-3">
                        État d&apos;<strong>insécurité perçue</strong>. Activation du système sympathique pour préparer 
                        le corps à fuir ou combattre. Libération d&apos;adrénaline et de cortisol.
                      </p>
                      <div className="p-3 bg-sky-50 rounded-lg">
                        <p className="text-sm text-slate-900 mb-1"><strong>Respiration :</strong></p>
                        <p className="text-sm text-slate-800">
                          <strong>Superficielle, haute fréquence (15-25 cycles/min), petits volumes</strong>. 
                          Respiration thoracique haute avec tensions dans le cou et les épaules. Hyperventilation chronique.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* State 3 - Dorsal Vagal (Immobilization) */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xl">3</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-medium mb-2">Nerf Vague Dorsal (Immobilisation : Figement)</h4>
                      <p className="text-slate-600 mb-3">
                        État de <strong>danger extrême perçu</strong> (vie menacée). Lorsque ni la fuite ni le combat 
                        ne sont possibles, le système nerveux active le mode le plus ancien : l&apos;immobilisation ou &ldquo;figement&rdquo; 
                        (freeze). État de dissociation, d&apos;engourdissement émotionnel.
                      </p>
                      <div className="p-3 bg-slate-100 rounded-lg">
                        <p className="text-sm text-slate-900 mb-1"><strong>Respiration :</strong></p>
                        <p className="text-sm text-slate-700">
                          <strong>Respiration irrégulière à grands volumes, avec soupirs et bâillements fréquents</strong>. 
                          Peut aussi se manifester par des <strong>apnées inconscientes durant la journée</strong> (retenir 
                          sa respiration sans s&apos;en rendre compte). Alternance entre respiration bloquée et grandes inspirations.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 bg-slate-50 border-l-4 border-red-500 mt-8">
              <CardContent className="p-6">
                <h3 className="font-bold text-sky-900 mb-3 text-lg"> Danger Réel vs Danger Imaginé</h3>
                <div className="text-sky-800 space-y-3">
                  <p className="font-semibold">
                    Notre cerveau ne fait PAS la différence entre un danger réel et un danger imaginé par l&apos;esprit.
                  </p>
                  <p>
                    Le système nerveux réagit de la même manière face à :
                  </p>
                  <ul className="ml-6 space-y-2 text-sm">
                    <li>• Un danger physique immédiat (ex: un chien qui attaque)</li>
                    <li>• Une situation stressante (ex: présentation au travail, conflit)</li>
                    <li>• Une pensée anxieuse (ex: rumination sur un événement passé ou futur)</li>
                    <li>• Un souvenir traumatique qui ressurgit</li>
                  </ul>
                  <p className="mt-4 font-semibold">
                    Les <strong>traumatismes qui se racontent dans notre subconscient</strong> ont un impact majeur sur 
                    l&apos;activation chronique de notre système sympathique (ou dorsal vagal en cas de trauma sévère).
                  </p>
                  <p>
                    Même si consciemment nous sommes &ldquo;en sécurité&rdquo;, notre système nerveux peut rester bloqué dans un état 
                    de menace si des mémoires traumatiques non résolues continuent d&apos;envoyer des signaux d&apos;alerte au cerveau. 
                    Ces traumatismes peuvent être :
                  </p>
                  <ul className="ml-6 space-y-1 text-sm">
                    <li>• Événements traumatiques majeurs (accidents, violences, abus)</li>
                    <li>• Traumatismes développementaux (négligence émotionnelle, attachement insécure)</li>
                    <li>• Stress chronique accumulé (épuisement professionnel, deuils non résolus)</li>
                    <li>• Traumatismes transgénérationnels (transmis de génération en génération)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-medium mt-8">Expressions Respiratoires Spécifiques selon le Mode de Survie</h3>

            <p className="text-lg">
              En fonction de notre fonctionnement individuel et de notre histoire traumatique, notre système nerveux 
              peut privilégier un mode de survie particulier, aboutissant à une <strong>expression respiratoire spécifique</strong> :
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-medium mb-3">Mode Fuite/Combat</h4>
                  <p className="text-sm text-slate-600 mb-3">
                    <strong>Pattern :</strong> Sympathique dominant
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="p-2 bg-sky-50 rounded">
                      <p className="font-semibold text-slate-900 mb-1">Respiration typique :</p>
                      <ul className="text-slate-800 space-y-1 ml-4">
                        <li>• Superficielle</li>
                        <li>• Haute fréquence (15-25/min)</li>
                        <li>• Petits volumes</li>
                        <li>• Thoracique haute</li>
                        <li>• Tensions cou/épaules</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center mb-4">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-medium mb-3">Mode Figement</h4>
                  <p className="text-sm text-slate-600 mb-3">
                    <strong>Pattern :</strong> Dorsal vagal dominant
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="p-2 bg-slate-100 rounded">
                      <p className="font-semibold text-slate-900 mb-1">Respiration typique :</p>
                      <ul className="text-slate-700 space-y-1 ml-4">
                        <li>• Grands volumes</li>
                        <li>• Soupirs fréquents</li>
                        <li>• Bâillements répétés</li>
                        <li>• Apnées inconscientes</li>
                        <li>• Respiration retenue</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center mb-4">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-medium mb-3">Mode Mixte</h4>
                  <p className="text-sm text-slate-600 mb-3">
                    <strong>Pattern :</strong> Alternance
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="p-2 bg-sky-50 rounded">
                      <p className="font-semibold text-slate-900 mb-1">Respiration typique :</p>
                      <ul className="text-slate-800 space-y-1 ml-4">
                        <li>• Oscillation entre les 2</li>
                        <li>• Hyperventilation puis apnée</li>
                        <li>• Instabilité respiratoire</li>
                        <li>• Difficulté à réguler</li>
                        <li>• Symptômes variables</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 bg-gradient-to-r from-sky-500 to-sky-600 text-white mt-8">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-4 text-center"> Comprendre pour Guérir</h3>
                <p className="text-center text-sky-100">
                  La théorie polyvagale nous montre que les troubles respiratoires ne sont pas &ldquo;dans la tête&rdquo;, 
                  mais des <strong>adaptations inconscientes du système nerveux</strong> face à des menaces perçues. 
                  La rééducation respiratoire doit donc viser à <strong>rétablir la sensation de sécurité</strong> 
                  dans le système nerveux, en plus de corriger les patterns respiratoires.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Problem - Chronic Activation */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Le Problème : L&apos;Activation Chronique du Mode Sympathique
          </h2>

          <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
            <Card className="border-0 bg-slate-50 border-l-4 border-red-500">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-sky-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-sky-900 mb-3 mt-0">Le Cercle Vicieux</h3>
                    <p className="text-sky-800 mb-0">
                      Dans notre société moderne, le système nerveux est constamment bombardé de stimuli qu&apos;il interprète 
                      comme des menaces : stress au travail, problèmes relationnels, inquiétudes financières, informations 
                      anxiogènes, traumatismes non résolus, manque de sommeil, inflammation chronique...
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="text-lg">
              Lorsque le mode sympathique reste activé de façon <strong>chronique</strong>, le corps reste en permanence 
              en état d&apos;alerte. Cette activation prolongée a des conséquences profondes sur l&apos;ensemble de l&apos;organisme, 
              et notamment sur la respiration.
            </p>

            <h3 className="text-2xl font-medium">Impact sur la Respiration</h3>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h4 className="font-medium mb-3">Modifications Physiologiques</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex gap-2">
                      <Activity className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                      <span><strong>Augmentation de la fréquence respiratoire</strong> pour fournir plus d&apos;oxygène</span>
                    </li>
                    <li className="flex gap-2">
                      <Activity className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                      <span><strong>Respiration thoracique haute</strong> au lieu de diaphragmatique</span>
                    </li>
                    <li className="flex gap-2">
                      <Activity className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                      <span><strong>Tensions musculaires</strong> dans le cou, les épaules, le thorax</span>
                    </li>
                    <li className="flex gap-2">
                      <Activity className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                      <span><strong>Hyperventilation chronique</strong> avec perte excessive de CO₂</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h4 className="font-medium mb-3">Conséquences Respiratoires</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex gap-2">
                      <AlertTriangle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                      <span>Syndrome d&apos;hyperventilation chronique</span>
                    </li>
                    <li className="flex gap-2">
                      <AlertTriangle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                      <span>Respiration dysfonctionnelle</span>
                    </li>
                    <li className="flex gap-2">
                      <AlertTriangle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                      <span>Sensation d&apos;oppression thoracique</span>
                    </li>
                    <li className="flex gap-2">
                      <AlertTriangle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                      <span>Soupirs fréquents et bâillements</span>
                    </li>
                    <li className="flex gap-2">
                      <AlertTriangle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                      <span>Sensation de manque d&apos;air paradoxale</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 bg-gradient-to-r from-sky-500 to-sky-600 text-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-center">La Clé de la Rééducation Respiratoire</h3>
                <p className="text-center text-sky-100 text-lg">
                  Comprendre que les troubles respiratoires ne sont souvent que le <strong>symptôme visible</strong> d&apos;un 
                  système nerveux autonome déséquilibré. La rééducation ne consiste pas seulement à &ldquo;mieux respirer&rdquo;, 
                  mais à <strong>ré-entraîner le système nerveux</strong> à retrouver un état de sécurité et d&apos;équilibre.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            La Solution : Rétablir l&apos;Équilibre
          </h2>

          <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
            <p className="text-lg">
              La bonne nouvelle est que le système nerveux est <strong>plastique</strong> : il peut être rééduqué et 
              ré-entraîné. Les techniques de rééducation respiratoire agissent directement sur le système nerveux autonome 
              pour restaurer l&apos;équilibre entre le mode sympathique et parasympathique.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-medium mb-2">Respiration Contrôlée</h4>
                  <p className="text-sm text-slate-600">
                    Active directement le nerf vague et le système parasympathique
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mx-auto mb-4">
                    <Activity className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-medium mb-2">Conscience Corporelle</h4>
                  <p className="text-sm text-slate-600">
                    Permet de détecter et relâcher les tensions chroniques
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-medium mb-2">Signal de Sécurité</h4>
                  <p className="text-sm text-slate-600">
                    Ré-apprend au système nerveux qu&apos;il est en sécurité
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Commencez Votre Rééducation
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Découvrez nos exercices conçus pour rétablir l&apos;équilibre de votre système nerveux autonome 
            et retrouver une respiration optimale.
          </p>
          <Link to="/app-landing">
            <button className="px-10 py-4 text-lg rounded-full bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg font-semibold transition-colors">
              Accéder aux exercices
            </button>
          </Link>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default CausesPage;
